import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { THEME_STORAGE_KEY } from "@/theme/theme";
import { useAuth } from "./AuthContext";

/** The app's own keys (`anthm.*`) that belong to this browser, not to the person signed in. */
const DEVICE_KEYS = new Set([
  THEME_STORAGE_KEY,
  "anthm.sidebar.favoritesCollapsed",
  "anthm.desktopPermissionPromptDismissed",
]);

function clearAppKeys(storage: Storage) {
  for (const key of Object.keys(storage)) {
    if (key.startsWith("anthm.") && !DEVICE_KEYS.has(key)) storage.removeItem(key);
  }
}

/**
 * Drops everything the app holds for the signed-in person: cached server data and the app's per-user browser
 * storage. Identity provider tokens (MSAL's cache) are left to the provider's own logout.
 */
export function clearUserData(queryClient: QueryClient) {
  queryClient.clear();
  clearAppKeys(localStorage);
  clearAppKeys(sessionStorage);
}

/**
 * Signs out, after clearing the previous person's data so none of it shows for whoever signs in next in this
 * tab (Google and dev sign-out don't reload the page).
 */
export function useSignOut(): () => Promise<void> {
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  return useCallback(() => {
    clearUserData(queryClient);
    return logout();
  }, [logout, queryClient]);
}
